import React, { forwardRef, ReactElement, InputHTMLAttributes } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Text, Box as Wrapper, Input } from '@tonic-ui/react';

import { FieldTextLabel, InlineError } from './index';

type Ref = null;
type InputFieldProps = {
  id: string;
  type: string;
  label?: string;
  name: string;
  helperText?: string;
  isRequired?: boolean;
  infoPopoverContent?: ReactElement;
} & InputHTMLAttributes<HTMLInputElement>;

const InputField = forwardRef<Ref, InputFieldProps>(
  (
    {
      id,
      placeholder = '',
      role,
      helperText = '',
      type = 'text',
      label,
      disabled = false,
      isRequired = false,
      infoPopoverContent,
      autoComplete = 'off',
      maxLength,
      ...rest
    }: InputFieldProps,
    ref
  ): JSX.Element => {
    const [field, meta, helpers] = useField(rest);
    const { setValue } = helpers;
    const isInvalid = meta.error && meta.touched ? meta.error : '';
    const invalidInputStyles = { error: isInvalid.length > 1 };
    const { t } = useTranslation();

    const inputId = id + '-input';

    return (
      <>
        {label && (
          <FieldTextLabel
            name={label}
            htmlFor={inputId}
            isRequired={isRequired}
            infoPopoverContent={infoPopoverContent}
          />
        )}
        <Input
          {...field}
          autoComplete={autoComplete}
          id={inputId}
          data-id={inputId}
          maxLength={maxLength}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          role={role}
          fontWeight={300}
          {...invalidInputStyles}
          onChange={(e) => {
            setValue(type === 'email' ? e.target.value.trim() : e.target.value, true);
          }}
          ref={ref}
        />
        {isInvalid && (
          <Text size="xs" mt="1x">
            <InlineError errorName={isInvalid} />
          </Text>
        )}
        {helperText && (
          <Wrapper marginTop="5px">
            <Text size="xs" lineHeight="xs">
              {t(`${helperText}`)}
            </Text>
          </Wrapper>
        )}
      </>
    );
  }
);

InputField.displayName = 'InputField';

export default InputField;
